import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Banknote, ChevronRight } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { CashCount } from '../../components/nursery/counter/CashCount';
import nurseryCounterService from '../../services/nurseryCounter';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { errorMessage } from '../../utils/apiResponse';
import { money } from '../../utils/nurseryMoney';
import { breakdownTotal, cleanBreakdown, parseAmount, varianceText } from '../../utils/counterCash';
import { formatDateTimeInTz, useOrgTimezone } from '../../utils/timezone';
import type { CashBreakdown, TillList, TillSession } from '../../types/nurseryCounter';

/**
 * The tills cash is taken into at the counter. A till is opened with a
 * counted float, takes cash sales and gives change, and is closed with a
 * count; what it should have held and the difference are kept with it.
 * Nothing recorded here is ever changed or deleted.
 */

const input = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm';

function Tills() {
  const timezone = useOrgTimezone();
  const navigate = useNavigate();
  const { hasAccess: canWork } = useModuleLicense('nursery', ['manager', 'admin']);
  const [list, setList] = useState<TillList | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [opening, setOpening] = useState(false);
  const [name, setName] = useState('Counter');
  const [count, setCount] = useState<CashBreakdown>({});
  const [floatTyped, setFloatTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await nurseryCounterService.listTills();
      if (res.success) setList(res.data);
      else setError(errorMessage(res, 'The tills could not be loaded'));
    } catch (e) {
      setError(errorMessage(e, 'The tills could not be loaded'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const counted = cleanBreakdown(count);
  const floatValue = counted ? breakdownTotal(counted) : parseAmount(floatTyped);

  const open = async () => {
    if (floatValue == null) return;
    setBusy(true);
    setOpenError(null);
    try {
      const res = await nurseryCounterService.openTill({ till_name: name.trim() || 'Counter', opening_float: floatValue, breakdown: counted });
      if (res.success) {
        navigate(`/nursery/tills/${res.data.id}`);
        return;
      }
      setOpenError(errorMessage(res, 'The till could not be opened'));
    } catch (e) {
      setOpenError(errorMessage(e, 'The till could not be opened'));
    }
    setBusy(false);
  };

  return (
    <>
      <PageHeader
        title="Tills"
        subtitle="Cash taken at the counter: the float, every sale and the count at the close"
        icon={<Banknote className="h-6 w-6 text-white" />}
        actionButtons={canWork && !opening ? (
          <Button size="sm" onClick={() => setOpening(true)}>Open a till</Button>
        ) : undefined}
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>}

        {opening && (
          <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-4" aria-label="Open a till">
            <h2 className="text-base font-semibold text-neutral-900">Open a till</h2>
            <div>
              <label htmlFor="till-name" className="block text-sm font-medium text-neutral-700 mb-1">Till</label>
              <input id="till-name" type="text" maxLength={60} value={name} onChange={(e) => setName(e.target.value)} className={input} />
              <p className="text-xs text-neutral-500 mt-1">The name of the drawer or counter, if there is more than one.</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-neutral-700">Count the float</p>
              <CashCount idPrefix="float" value={count} onChange={setCount} denominations={list?.denominations} />
              {!counted && (
                <div>
                  <label htmlFor="till-float" className="block text-sm font-medium text-neutral-700 mb-1">Or type the float</label>
                  <input id="till-float" type="text" inputMode="decimal" value={floatTyped}
                    onChange={(e) => setFloatTyped(e.target.value.replace(/[^\d.]/g, ''))} className={input} placeholder="0.00" />
                </div>
              )}
              <p className="text-sm text-neutral-800">
                Float: <span className="font-semibold">{floatValue != null ? money(floatValue) : '—'}</span>
              </p>
            </div>
            {openError && <p className="text-sm text-red-700 break-words" role="alert">{openError}</p>}
            <div className="flex flex-wrap gap-2">
              <Button onClick={open} disabled={busy || floatValue == null}>{busy ? 'Opening…' : 'Open the till'}</Button>
              <Button variant="outline" onClick={() => setOpening(false)} disabled={busy}>Cancel</Button>
            </div>
          </section>
        )}

        {list && (
          <>
            <section className="space-y-2">
              <h2 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">Open</h2>
              {list.open.length === 0 ? (
                <p className="text-sm text-neutral-600 bg-white border border-neutral-200 rounded-xl px-4 py-6 text-center">
                  No till is open. Cash cannot be taken until one is.
                </p>
              ) : list.open.map((s) => <TillRow key={s.id} session={s} timezone={timezone} />)}
            </section>
            <section className="space-y-2">
              <h2 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">Closed, last 90 days</h2>
              {list.closed.length === 0
                ? <p className="text-sm text-neutral-500">None yet.</p>
                : list.closed.map((s) => <TillRow key={s.id} session={s} timezone={timezone} />)}
            </section>
          </>
        )}
      </div>
    </>
  );
}

function TillRow({ session: s, timezone }: { session: TillSession; timezone: string }) {
  const t = s.totals;
  const out = s.variance != null && Math.abs(s.variance) >= 0.005;
  return (
    <Link to={`/nursery/tills/${s.id}`}
      className="flex items-center gap-3 bg-white border border-neutral-200 rounded-xl px-4 py-3 hover:border-primary-300 min-w-0">
      <div className="min-w-0 flex-1">
        <p className="font-medium text-neutral-900 break-words">
          {s.till_name}
          <span className="font-normal text-neutral-500"> · opened {formatDateTimeInTz(s.opened_at, timezone)}{s.opened_by_name ? ` by ${s.opened_by_name}` : ''}</span>
        </p>
        <p className="text-sm text-neutral-600 break-words">
          {s.status === 'open'
            ? `Should hold ${money(t?.expected_cash ?? s.opening_float)} · ${t?.sale_count ?? 0} cash sale${t?.sale_count === 1 ? '' : 's'}`
            : `Counted ${money(s.counted_cash ?? 0)} against ${money(s.expected_cash ?? 0)}`}
          {s.status === 'closed' && (
            <span className={out ? 'text-red-700 font-medium' : 'text-green-700'}> · {varianceText(s.variance)}</span>
          )}
        </p>
      </div>
      <ChevronRight className="h-4 w-4 text-neutral-400 shrink-0" />
    </Link>
  );
}

export default Tills;
